import { useEffect, useRef } from 'react';

/**
 * Could easily be replaced by a hook parameter if a variable value is needed.
 */
const TIMEOUT = 2000;

/**
 * When the user stops scrolling, queue a call to the given function.
 * If the user starts scrolling again before the timeout, the queued function call is cancelled and a new function call is queued.
 * Having the given function change identity (e.g. by redefining it with a new arrow function on every render) is fine and should not impact performance.
 */
function useScrollStopDetector(fn) {
  /**
   * Allow the function to change identity often without impacting performance.
   * By keeping the function in a ref it doesn't need to be a dependency of the effect hook below.
   */
  const fnRef = useRef(fn);

  /**
   * Update the function reference on every render, having minimal performance impact.
   */
  useEffect(() => {
    fnRef.current = fn;
  });

  useEffect(() => {
    /**
     * Reference to the timeout so it can be cleared on unmount or if the user starts scrolling again.
     */
    let timeout;

    const onScroll = () => {
      clearTimeout(timeout);
      timeout = setTimeout(() => {
        fnRef.current();
      }, TIMEOUT);
    };

    document.addEventListener('scroll', onScroll, false);

    /**
     * On desktop it is not the document that scrolls, but another 'root' element.
     */
    // eslint-disable-next-line no-unused-expressions
    document
      .getElementById('root')
      ?.addEventListener('scroll', onScroll, false);

    return () => {
      document.removeEventListener('scroll', onScroll, false);

      // eslint-disable-next-line no-unused-expressions
      document
        .getElementById('root')
        ?.removeEventListener('scroll', onScroll, false);

      clearTimeout(timeout);
    };
  }, []);
}

export default useScrollStopDetector;
